<template>
  <v-dialog width="350">
    <template #activator="{ props }">
      <div
        class="pa-3 text-center"
        :class="merchantsStore.overridden && 'staff-warning-blink'"
        style="cursor: pointer"
        v-bind="props"
      >
        {{ merchantsStore.currentMerchant?.legalEntityName }}
      </div>
    </template>

    <template #default="{ isActive }">
      <v-card :title="t('components.companies.staffOverride.title')">
        <v-form @submit.prevent>
          <v-container>
            <v-combobox
              v-model="overrideMerchant"
              :items="merchants"
              :loading="isLoadingMerchants"
              :label="t('components.companies.staffOverride.searchLabel')"
              item-title="legalEntityName"
              item-value="merchantId"
              hide-details
              solo
              clearable
              return-object
              @click:clear="merchants = []"
              @update:search="(name) => fetchMerchants({ name })"
            />
          </v-container>

          <v-card-text>
            {{ t("components.companies.staffOverride.customerIdLabel") }}
            <v-text-field
              :model-value="overrideMerchant?.merchantId || ''"
              autofocus
              @update:model-value="
                (v) => (overrideMerchant = { merchantId: v, legalEntityName: '' })
              "
            />
          </v-card-text>

          <v-card-actions>
            <v-btn-nd :text="t('form.cancel')" @click="isActive.value = false" />
            <v-spacer />
            <v-btn
              type="submit"
              :text="t('form.confirm')"
              class="bg-primary on-primary"
              @click="changeMerchant(() => (isActive.value = false))"
            />
          </v-card-actions>
        </v-form>
      </v-card>
    </template>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import { Merchant } from "@/api/merchant-service/merchant";
import { useYetipayApi } from "@/composables/useYetipayApi";
import { useMerchantsStore } from "@/store/merchants";

const { t } = useI18n();
const { api } = useYetipayApi();
const merchantsStore = useMerchantsStore();

const merchants = ref<Partial<Merchant>[]>([]);
const overrideMerchant = ref<Partial<Merchant> | null>(null);
const isLoadingMerchants = ref(false);

const fetchMerchants = async ({
  name,
  accountHolderId,
}: {
  name?: string;
  accountHolderId?: string;
}) => {
  if (!name && !accountHolderId) {
    return;
  }

  isLoadingMerchants.value = true;
  try {
    const fetched = await api.queryMerchants(name, accountHolderId);
    merchants.value =
      fetched.data.data?.map(({ merchantId, legalEntityName }) => ({
        merchantId,
        legalEntityName,
      })) ?? [];
  } catch (error) {
    console.error(t("components.companies.staffOverride.searchError"), error);
  } finally {
    isLoadingMerchants.value = false;
  }
};

const changeMerchant = async (closeDialog: () => void) => {
  if (overrideMerchant.value?.merchantId?.startsWith("AH")) {
    await fetchMerchants({ accountHolderId: overrideMerchant.value.merchantId });
    if (merchants.value.length === 1) {
      merchantsStore.override(merchants.value[0].merchantId!);
    }
  } else if (overrideMerchant.value) {
    merchantsStore.override(overrideMerchant.value.merchantId!);
  }
  closeDialog();
};
</script>
